import { useMemo, useRef, useState } from 'react';
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
  DownChevronIcon,
  ProgressSpinner,
  SearchIcon,
} from '@repo/atomic-components';
import { ChatShareCandidate } from '@/api/chatSharing';

export interface ChatShareRecipientSelectProps {
  inputId: string;
  candidates: ChatShareCandidate[];
  /** The text each suggestion shows and is searched by. */
  getLabel: (candidate: ChatShareCandidate) => string;
  /** Ids already in the access list, hidden from the suggestions. */
  excludedUserIds: ReadonlySet<string>;
  emptyMessage: string;
  onAdd: (candidate: ChatShareCandidate) => void;
  disabled?: boolean;
  /** Shows the pending state inside the field, which keeps the dialog from resizing while it loads. */
  isLoading?: boolean;
}

export const ChatShareRecipientSelect = ({
  inputId,
  candidates,
  getLabel,
  excludedUserIds,
  emptyMessage,
  onAdd,
  disabled = false,
  isLoading = false,
}: ChatShareRecipientSelectProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const autoCompleteRef = useRef<AutoComplete>(null);

  /** The department list is small enough to filter here, and the endpoint takes no search term. */
  const suggestions = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return candidates.filter(
      (candidate) =>
        !excludedUserIds.has(candidate.userId) && (!needle || getLabel(candidate).toLowerCase().includes(needle)),
    );
  }, [candidates, excludedUserIds, getLabel, query]);

  const openSuggestions = () => {
    if (disabled) {
      return;
    }
    (autoCompleteRef.current as unknown as { show?: () => void } | null)?.show?.();
  };

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    if (isOpen) {
      (autoCompleteRef.current as unknown as { hide?: () => void } | null)?.hide?.();
      return;
    }
    setQuery('');
    openSuggestions();
  };

  return (
    <div className="border-theme-border-grey bg-theme-white flex w-full items-center gap-[0.4375rem] rounded-sm border px-2 py-1.5">
      <SearchIcon className="text-theme-text-grey h-[16px] w-[16px] shrink-0" aria-hidden />

      <AutoComplete
        ref={autoCompleteRef}
        inputId={inputId}
        disabled={disabled}
        value={query}
        suggestions={suggestions}
        field="name"
        itemTemplate={(candidate: ChatShareCandidate) => getLabel(candidate)}
        delay={0}
        minLength={1}
        loadingIcon={<></>}
        placeholder="Search for user"
        showEmptyMessage
        emptyMessage={emptyMessage}
        completeMethod={(event: AutoCompleteCompleteEvent) => setQuery(event.query ?? '')}
        onChange={(event: AutoCompleteChangeEvent) => {
          if (typeof event.value === 'string') {
            setQuery(event.value);
          }
        }}
        onSelect={(event) => {
          onAdd(event.value as ChatShareCandidate);
          setQuery('');
        }}
        onShow={() => setIsOpen(true)}
        onHide={() => setIsOpen(false)}
        className="min-w-0 flex-1"
        inputClassName="!h-5 !w-full !border-0 !bg-transparent !p-0 !text-sm !leading-5 !text-theme-black !shadow-none !outline-none focus:!ring-0 placeholder:!text-theme-text-grey"
        pt={{
          emptyMessage: { className: '!text-theme-text-grey !px-3 !py-2 !text-sm' },
        }}
      />

      {isLoading ? (
        <div
          className="flex h-4 w-4 shrink-0 items-center justify-center"
          role="status"
          aria-label="Loading people with access"
        >
          <ProgressSpinner style={{ width: '12px', height: '12px' }} strokeWidth="4" animationDuration="1.5s" />
        </div>
      ) : (
        <button
          type="button"
          aria-label="Show sharing options"
          disabled={disabled}
          className="text-theme-text-grey flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center disabled:cursor-default disabled:opacity-60"
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleToggle}
        >
          <DownChevronIcon className={`h-[16px] w-[16px] ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
};
